import { defineStore } from 'pinia';
import { ref } from 'vue';

// Which treatment the shell snackbar renders. All three share one surface
// and one queue — they are mutually exclusive states of the same "something
// changed on an item you have a stake in" notification.
//   'outbid'      — someone outbid you (bid.outbid).
//   'removed'     — an organizer removed YOUR bid (GT1-442, bid.voided).
//                   "Removed" is the only bidder-facing word for a void, and
//                   the admin's free-text reason is never surfaced.
//   'now-winning' — a void promoted YOU to high bidder (GT1-442, bid.voided).
export type BidToastVariant = 'outbid' | 'removed' | 'now-winning';

// Snackbar payload for the shell-level bid toast. itemTitle is best-
// effort: when the bidder is somewhere with the item in cache we pass a
// real title; otherwise the snackbar falls back to a generic message.
// itemId/bundleId mirror the bid.outbid / bid.voided wire frames — exactly
// one is set.
export interface OutbidToastPayload {
  itemId: number | null;
  bundleId: number | null;
  itemTitle: string | null;
  // Entity high bid AFTER the event that triggered the toast:
  //   'outbid'      → the competing bid that beat you
  //   'removed'     → the price the item fell back to (0 = no bids left)
  //   'now-winning' → your own bid, which now leads
  newHighBidCents: number;
  eventSlug: string;
  variant: BidToastVariant;
}

// itemId/bundleId are independent PK spaces that can numerically collide
// (an item #5 and a bundle #5 are unrelated entities) — always key
// suppression/dedupe by this composite string, never by the raw numeric id.
function entityKey(itemId: number | null, bundleId: number | null): string {
  return itemId != null ? `item:${itemId}` : `bundle:${bundleId}`;
}

function sameEntity(a: OutbidToastPayload, b: OutbidToastPayload): boolean {
  return entityKey(a.itemId, a.bundleId) === entityKey(b.itemId, b.bundleId);
}

const MAX_QUEUE = 5;

export const useOutbidToast = defineStore('outbidToast', () => {
  const current = ref<OutbidToastPayload | null>(null);
  // Backlog of outbid toasts waiting to be shown once `current` clears —
  // rapid consecutive outbids on different items/bundles no longer
  // overwrite each other silently.
  const queue = ref<OutbidToastPayload[]>([]);

  // Lane C1 — per-entity suppression window. When a BidSheet placement
  // returns an immediate-outbid tie, the in-sheet result panel is the
  // authoritative surface; the standard outbid snackbar that would
  // arrive moments later (real wire) or simultaneously (mock) is just
  // noise. We suppress the toast for that entity for a short window
  // (typically 5s) so the in-sheet result is the only signal.
  //
  // Keyed by entityKey() → unix-ms suppression expiry. show() consults
  // it and silently no-ops if the toast falls within the window.
  const suppressedUntil = ref<Map<string, number>>(new Map());

  function isSuppressed(itemId: number | null, bundleId: number | null): boolean {
    const key = entityKey(itemId, bundleId);
    const until = suppressedUntil.value.get(key);
    if (until == null) return false;
    if (Date.now() >= until) {
      suppressedUntil.value.delete(key);
      return false;
    }
    return true;
  }

  // The suppression window exists for ONE case: the BidSheet result panel is
  // the authoritative surface for an immediate-outbid tie. It must not swallow
  // an organizer's bid removal or a promotion to high bidder — those have no
  // competing in-sheet surface, and silently dropping "Your bid was removed"
  // would leave the bidder believing their bid still stands (GT1-442).
  function isSuppressedFor(payload: OutbidToastPayload): boolean {
    if (payload.variant !== 'outbid') return false;
    return isSuppressed(payload.itemId, payload.bundleId);
  }

  function advance(): void {
    // Re-check suppression here, not just in show() — a toast can sit in the
    // queue for a while, and suppressFor() may be armed for its entity after
    // it was queued but before it's dequeued (e.g. the BidSheet tie-result
    // panel closes while an earlier toast is still displayed).
    while (queue.value.length > 0) {
      const next = queue.value.shift()!;
      if (!isSuppressedFor(next)) {
        current.value = next;
        return;
      }
    }
    current.value = null;
  }

  function show(payload: OutbidToastPayload): void {
    if (isSuppressedFor(payload)) return;
    // A fresh outbid on the entity already being displayed just updates
    // the amount in place rather than requeuing behind itself.
    if (current.value && sameEntity(current.value, payload)) {
      current.value = payload;
      return;
    }
    const queued = queue.value.findIndex((p) => sameEntity(p, payload));
    if (queued !== -1) {
      queue.value[queued] = payload;
    } else {
      queue.value.push(payload);
      if (queue.value.length > MAX_QUEUE) queue.value.shift();
    }
    if (!current.value) advance();
  }

  function dismiss(): void {
    advance();
  }

  function suppressFor(itemId: number | null, bundleId: number | null, ms: number): void {
    suppressedUntil.value.set(entityKey(itemId, bundleId), Date.now() + ms);
  }

  function clearSuppression(): void {
    suppressedUntil.value = new Map();
  }

  return { current, queue, show, dismiss, suppressFor, clearSuppression, isSuppressed };
});
